import { useCallback } from 'react';

import { useRouteLoaderData } from '@remix-run/react';

import type { RootLoaderData } from '~/root';

import { useCommunityMembership } from './useCommunityMembership';

const REPORT_ABUSE_PREFILL_URL =
  import.meta.env.VITE_REPORT_ABUSE_PREFILL_URL ?? 'https://www.beehiiv.com/api/report_abuse_prefill';

// Escalates a community report to beehiiv's abuse form. POSTs the prefill values
// (form-encoded) to the marketing site, which stashes them in a cookie and
// redirects to the report_abuse form. A POST keeps a long reason out of the URL.
export const useReportToBeehiiv = () => {
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const publication = site.publication;
  const { currentMember } = useCommunityMembership();

  return useCallback(
    (reason?: string, reportablePath?: string) => {
      const fields: Record<string, string> = { publication: publication.url, code: 'other' };

      const subscription = currentMember?.subscription;
      if (subscription?.email) fields.reporter_email = subscription.email;
      if (subscription?.first_name) fields.reporter_first_name = subscription.first_name;
      if (subscription?.last_name) fields.reporter_last_name = subscription.last_name;

      // The form has no dedicated content-link field, so prepend the link to the
      // comment (first, so it survives the form's length cap) then the reason.
      const link = reportablePath ? `${window.location.origin}${reportablePath}` : undefined;
      const comments = [link && `Reported content: ${link}`, reason?.trim()].filter(Boolean).join('\n\n');
      if (comments) fields.comments = comments;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = REPORT_ABUSE_PREFILL_URL;
      form.target = '_blank';
      for (const [name, value] of Object.entries(fields)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
      form.remove();
    },
    [publication.url, currentMember],
  );
};
